import { useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { DollarSign, Award, Clock, Star } from "lucide-react";

/** Render the list of bids on a resident-owned ticket and let the resident accept one. */
export default function TicketBids({ ticket, onUpdate }) {
  const bids = ticket.bids || [];
  const [accepting, setAccepting] = useState(null);
  const winner = bids.find((b) => b.status === "accepted");

  const accept = async (bidId) => {
    setAccepting(bidId);
    try {
      await api.post(`/maintenance/${ticket.id}/bids/${bidId}/accept`);
      toast.success("Bid accepted — vendor notified");
      onUpdate?.();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setAccepting(null);
  };

  return (
    <div className="mt-4 rounded-md border border-black/5 dark:border-white/10 bg-zinc-50/70 dark:bg-white/[0.03] p-3">
      <div className="flex items-center gap-2 mb-2">
        <div className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1">
          <DollarSign className="h-3 w-3" /> Bids ({bids.length})
        </div>
        {winner && (
          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200">
            <Award className="h-3 w-3 mr-1 inline" /> Winner picked
          </Badge>
        )}
      </div>
      {bids.length === 0 ? (
        <div className="text-xs text-zinc-500 italic">
          No bids yet. Vendors bid faster when they can see your photos.
        </div>
      ) : (
        <div className="space-y-2">
          {bids.map((b) => (
            <div
              key={b.id}
              data-testid={`bid-row-${b.id}`}
              className={"flex items-center justify-between rounded-md border p-3 bg-white dark:bg-white/[0.02] " +
                (b.status === "accepted" ? "border-emerald-300 shadow-[0_0_0_1px_rgba(16,185,129,0.35)]" :
                 b.status === "declined" ? "border-black/5 opacity-60 dark:border-white/5" :
                 "border-black/5 dark:border-white/10")}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium truncate">{b.vendor_name}</div>
                  {b.vendor_category && <Badge variant="outline" className="text-[10px]">{b.vendor_category}</Badge>}
                  <Badge
                    variant="outline"
                    className={
                      b.status === "accepted" ? "border-emerald-300 text-emerald-700 bg-emerald-50" :
                      b.status === "declined" ? "border-zinc-300 text-zinc-500" :
                      "border-black/10"
                    }
                  >
                    {b.status}
                  </Badge>
                </div>
                {b.note && <div className="text-xs text-zinc-600 dark:text-zinc-400 mt-0.5 truncate">{b.note}</div>}
                {b.eta_days !== null && b.eta_days !== undefined && (
                  <div className="text-[11px] text-zinc-500 flex items-center gap-1 mt-0.5">
                    <Clock className="h-3 w-3" /> ETA {b.eta_days} day{b.eta_days === 1 ? "" : "s"}
                  </div>
                )}
              </div>
              <div className="text-right ml-3 shrink-0">
                <div className="text-xl font-semibold font-display tabular-nums">
                  ${Number(b.amount).toFixed(2)}
                </div>
                {b.status === "accepted" && b.review_stars ? (
                  <div className="flex items-center justify-end gap-0.5 mt-1" title={`${b.review_stars}/5 review`}>
                    {[1,2,3,4,5].map((n) => (
                      <Star key={n} className={"h-3.5 w-3.5 " + (n <= b.review_stars ? "text-amber-400 fill-amber-400" : "text-zinc-300")} />
                    ))}
                  </div>
                ) : null}
                {!winner && (
                  <Button
                    size="sm"
                    onClick={() => accept(b.id)}
                    disabled={accepting === b.id}
                    data-testid={`accept-bid-${b.id}`}
                    className="mt-1 rounded-full bg-zinc-900 hover:bg-zinc-800 text-white"
                  >
                    {accepting === b.id ? "…" : "Pick"}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
